import type { VariantProps } from '@stitches/react'
import React from 'react'

import { css } from 'design'
import { cn } from 'util/cn'

// This is a custom button style for this particular action
export const onboardingButtonStyles = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '0 $4',
  borderRadius: '$3',
  color: '$foreground',
  fill: '$foreground',
  fontSize: '$1',
  fontWeight: 'bold',
  lineHeight: 1.3,
  minHeight: 40,
  cursor: 'pointer',

  '> * + *': {
    marginLeft: '$2',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    backgroundColor: 'inherit',
    borderRadius: 'inherit',
    opacity: 0,
    width: '100%',
    height: '100%',
    left: 0,
    top: 0,
    pointerEvents: 'none',
  },

  '@md': {
    fontSize: '$2',
  },

  variants: {
    variant: {
      flamboyant: {
        backgroundImage:
          'radial-gradient(circle at 19% -136%, #05d5f8, #037be9 75%)',
        backgroundSize: '100% 100%',
        backgroundPosition: '0% 0%',
        border: 'none',

        '&:disabled': {
          pointerEvents: 'none',

          '&::after': {
            opacity: 0.4,
          },
        },
      },
      subdued: {
        backgroundColor: '$backgroundSecondary',
        border: '1px solid $grey300',

        '&:disabled': {
          border: '1px solid $grey500',
          pointerEvents: 'none',

          '&::after': {
            opacity: 0.8,
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'flamboyant',
  },
})

export type OnboardingButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> &
  VariantProps<typeof onboardingButtonStyles>

export const OnboardingButton = React.forwardRef<
  HTMLButtonElement,
  OnboardingButtonProps
>(({ children, variant, className, ...props }, ref) => (
  <button
    {...props}
    className={cn(className, onboardingButtonStyles({ variant }))}
    ref={ref}
  >
    {children}
  </button>
))

OnboardingButton.displayName = 'OnboardingButton'
